import { wallOpeningObb, wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import { SwingSide, type GeneratedDoorPlacement, type DoorOperationInfo } from "building-types/doors/doorTypes";
import { WALL_THICKNESS_INTERIOR } from "common/constants/buildingObjects";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import { arc2ByCenterStartAngle } from "geom/arc";
import { PI } from "geom/core";
import { line2Eval, line2PerpDistance } from "geom/line2";
import { line2VecPerp, lineLength, lineStartPt, lineEndPt, line2SVL } from "geom/line2Polyline2";
import { mat3Mult, mat3Rotate, mat3Translate } from "geom/mat3";
import { obb2ByThickenedEdge } from "geom/obb2";
import { polyLine2ThickenedEdge } from "geom/offset";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeNormal,
  polyLine2EdgeTangent,
  polyLine2Extend,
  polyLine2Length,
  polyLine2Move,
  polyLine2MoveCopy,
  polyLine2Reverse,
  polyLine2Rotate,
  polyLine2Transform,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2AngleBetween, vec2Dist, vec2Normalize, vec2Perp, vec2Rotate } from "geom/vec2";
import { doorBackOpening3, doorFrontOpening3 } from "./doorQuery";

const DEFAULT_DOOR_PANEL_THICKNESS = 1.5;
const DEFAULT_DOOR_PANEL_HALF_THICKNESS = DEFAULT_DOOR_PANEL_THICKNESS / 2;

function doorOperationInfoNew({
  planOutlineInteriorLinework = [],
  planOutlineExteriorLinework = [],
  sliderOperationArrowLinework = [],
  doorSwingLinework = [],
  doorBodyLinework = [],
  doorBodyFixedLinework = [],
  overheadOperationLinework = [],
  operationLinework = [],
  doorBodyOBBs = [],
  doorSwingLineworkOBBs = [],
  frontRightSwing = undefined,
  frontLeftSwing = undefined,
  backRightSwing = undefined,
  backLeftSwing = undefined,
  frontRightIsLatchSide = false,
  frontLeftIsLatchSide = false,
  doorSwingStopsAtCorners = false,
}: Partial<DoorOperationInfo>): DoorOperationInfo {
  return {
    planOutlineInteriorLinework,
    planOutlineExteriorLinework,
    sliderOperationArrowLinework,
    doorSwingLinework,
    doorBodyLinework,
    doorBodyFixedLinework,
    overheadOperationLinework,
    operationLinework,
    doorBodyOBBs,
    doorSwingLineworkOBBs,
    frontRightSwing,
    frontLeftSwing,
    backRightSwing,
    backLeftSwing,
    frontRightIsLatchSide,
    frontLeftIsLatchSide,
    doorSwingStopsAtCorners,
  };
}

export function makeDoorOperationInfo(door: GeneratedDoorPlacement): DoorOperationInfo | null {
  if (!door.opening) {
    return null;
  }

  switch (door.descriptor.operationType) {
    case DoorOperationType.OPEN:
    case DoorOperationType.PORTICO:
      const obb = wallOpeningObb(door.opening);
      return doorOperationInfoNew({
        planOutlineInteriorLinework: [wallOpeningPlanExtents(door.opening)],
        doorBodyOBBs: obb ? [obb] : [],
      });
    case DoorOperationType.ARC: {
      if (!door.descriptor.requiresDoorSwing) {
        const obb = wallOpeningObb(door.opening);
        return doorOperationInfoNew({
          planOutlineInteriorLinework: [wallOpeningPlanExtents(door.opening)],
          doorBodyOBBs: obb ? [obb] : [],
        });
      }
      break;
    }
    // baseline swingangle side and plan outline exterior linework are not used
    case DoorOperationType.BYPASS:
      return makeBypassDoorOperationInfo(door);
    case DoorOperationType.BI_FOLD:
    case DoorOperationType.DOUBLE_BI_FOLD:
      return makeBiFoldDoorOperationInfo(door);
    case DoorOperationType.DOUBLE_FIXED_PANEL:
      return makeDoubleFixedPanelDoorOperationInfo(door, 45);
    case DoorOperationType.WALL_MOUNTED:
      return makeWallMountedDoorOperationInfo(door);
    case DoorOperationType.GARAGE_SINGLE:
    case DoorOperationType.GARAGE_DOUBLE:
      return makeGarageDoorOperationInfo(door);
    case DoorOperationType.POCKET:
      return makePocketDoorOperationInfo(door);
    case DoorOperationType.FIREPLACE:
    case DoorOperationType.CLOSET:
    case DoorOperationType.ATTIC:
    case DoorOperationType.DOUBLE:
    default:
  }

  //single swing door linework
  if (door.descriptor.doorLeafCount === 1 && door.descriptor.requiresDoorSwing) {
    return makeSingleSwingDoorOperationInfo(door, 45);
  }

  //double swing door linework
  if (door.descriptor.doorLeafCount > 1 && door.descriptor.requiresDoorSwing) {
    return makeDoubleSwingDoorOperationInfo(door, 45);
  }

  if (door.descriptor.isExterior) {
    const obb = wallOpeningObb(door.opening);
    return doorOperationInfoNew({
      planOutlineExteriorLinework: [polyLine2Edge(wallOpeningPlanExtents(door.opening), 0)],
      doorBodyOBBs: obb ? [obb] : [],
    });
  }

  return null;
}

function getDoorPlanOutlineExteriorLinework(door: GeneratedDoorPlacement) {
  return door.descriptor.isExterior && door?.opening ? [polyLine2Edge(wallOpeningPlanExtents(door.opening), 0)] : [];
}

function getDoorOperationSide(door: GeneratedDoorPlacement) {
  return door.computedSwingSide !== SwingSide.RIGHT;
}

function getDoorOperationBaseline(door: GeneratedDoorPlacement) {
  let locatedOutline = doorFrontOpening3(door, false);
  if (door.reverse) {
    locatedOutline = doorBackOpening3(door, false);
  }

  assertIsNotUndefined(locatedOutline);

  const baseline = polyLine2ByTwoPoints(locatedOutline[0], locatedOutline[1], locatedOutline[3], locatedOutline[4]);
  return baseline;
}

function getPocketDoorOperationBaseline(door: GeneratedDoorPlacement) {
  const frontOpening3 = doorFrontOpening3(door, false);
  const backOpening3 = doorBackOpening3(door, false);

  assertIsNotUndefined(frontOpening3);
  assertIsNotUndefined(backOpening3);

  const openingHalfDepth = vec2Dist(frontOpening3[0], frontOpening3[1], backOpening3[3], backOpening3[4]) / 2;

  const openingHalfDepthPlusPanelHalfThickness = openingHalfDepth + DEFAULT_DOOR_PANEL_HALF_THICKNESS;

  if (!door.reverse) {
    const [nx, ny] = vec2Perp(frontOpening3[0] - frontOpening3[3], frontOpening3[1] - frontOpening3[4]);
    const [normx, normy] = vec2Normalize(nx, ny);
    const baseline = polyLine2ByTwoPoints(frontOpening3[0], frontOpening3[1], frontOpening3[3], frontOpening3[4]);
    return polyLine2Move(
      baseline,
      normx * openingHalfDepthPlusPanelHalfThickness,
      normy * openingHalfDepthPlusPanelHalfThickness,
    );
  }

  const [nx, ny] = vec2Perp(backOpening3[0] - backOpening3[3], backOpening3[1] - backOpening3[4]);
  const [normx, normy] = vec2Normalize(nx, ny);
  const baseline = polyLine2ByTwoPoints(backOpening3[0], backOpening3[1], backOpening3[3], backOpening3[4]);
  return polyLine2Move(
    baseline,
    normx * openingHalfDepthPlusPanelHalfThickness,
    normy * openingHalfDepthPlusPanelHalfThickness,
  );
}

function makeDoubleSwingDoorOperationInfo(door: GeneratedDoorPlacement, swingAngle: number) {
  const baseline = getDoorOperationBaseline(door);

  const [midX, midY] = line2Eval(baseline[0], baseline[1], baseline[2], baseline[3], 0.5, true);
  const firstLine = polyLine2New([baseline[0], baseline[1], midX, midY]);
  const {
    doorBodyLinework: firstDoorBodyLinework,
    doorSwingLinework: firstDoorSwingLinework,
    doorBodyOBB: firstDoorBodyOBB,
    doorSwingLineworkOBB: firstDoorSwingLineworkOBB,
  } = lineworkDoorSingle(firstLine, true, swingAngle);

  const secondLine = polyLine2New([midX, midY, baseline[2], baseline[3]]);

  const {
    doorBodyLinework: secondDoorBodyLinework,
    doorSwingLinework: secondDoorSwingLinework,
    doorBodyOBB: secondDoorBodyOBB,
    doorSwingLineworkOBB: secondDoorSwingLineworkOBB,
  } = lineworkDoorSingle(secondLine, false, swingAngle);

  const reverse = door.reverse;
  const doorHalfWidth = door.descriptor.width / 2;

  return doorOperationInfoNew({
    doorBodyLinework: [...firstDoorBodyLinework, ...secondDoorBodyLinework],
    doorSwingLinework: [...firstDoorSwingLinework, ...secondDoorSwingLinework],
    planOutlineExteriorLinework: getDoorPlanOutlineExteriorLinework(door),
    frontRightSwing: !reverse ? doorHalfWidth : undefined,
    frontLeftSwing: !reverse ? doorHalfWidth : undefined,
    backRightSwing: reverse ? doorHalfWidth : undefined,
    backLeftSwing: reverse ? doorHalfWidth : undefined,
    doorBodyOBBs: [firstDoorBodyOBB, secondDoorBodyOBB],
    doorSwingLineworkOBBs: [firstDoorSwingLineworkOBB, secondDoorSwingLineworkOBB],
  });
}

function makeSingleSwingDoorOperationInfo(door: GeneratedDoorPlacement, swingAngle: number) {
  const { doorBodyLinework, doorSwingLinework, doorBodyOBB, doorSwingLineworkOBB } = lineworkDoorSingle(
    getDoorOperationBaseline(door),
    getDoorOperationSide(door),
    swingAngle,
  );

  const reverse = door.reverse;
  const side = getDoorOperationSide(door);
  const doorWidth = door.descriptor.width;

  return doorOperationInfoNew({
    doorBodyLinework,
    planOutlineExteriorLinework: getDoorPlanOutlineExteriorLinework(door),
    doorSwingLinework,
    frontLeftIsLatchSide: !side,
    frontRightIsLatchSide: side,
    frontRightSwing: !reverse && !side ? doorWidth : undefined,
    frontLeftSwing: !reverse && side ? doorWidth : undefined,
    backRightSwing: reverse && !side ? doorWidth : undefined,
    backLeftSwing: reverse && side ? doorWidth : undefined,
    doorBodyOBBs: [doorBodyOBB],
    doorSwingLineworkOBBs: [doorSwingLineworkOBB],
  });
}

function makeBiFoldDoorOperationInfo(door: GeneratedDoorPlacement) {
  const baseline = getDoorOperationBaseline(door);
  const side = getDoorOperationSide(door);
  const panelCount = 2;
  const panelAngle = PI / 2;
  const offset = -DEFAULT_DOOR_PANEL_THICKNESS;

  const [shiftX, shiftY] = line2VecPerp(baseline, true);
  polyLine2Move(baseline, shiftX * offset, shiftY * offset);

  const panelLength =
    door.descriptor.operationType === DoorOperationType.BI_FOLD
      ? lineLength(baseline) / panelCount
      : lineLength(baseline) / (panelCount * 2);

  const doorBodyLinework: PolyLine2[] = [];

  //folding doors from left jamb
  if (
    (side && door.descriptor.operationType === DoorOperationType.BI_FOLD) ||
    door.descriptor.operationType === DoorOperationType.DOUBLE_BI_FOLD
  ) {
    const leftPanelsStart = lineStartPt(baseline);
    const doorPanelLine = doorPanelBaseLine(baseline, (3 * PI) / 4, leftPanelsStart, panelLength);
    const { doorBodyLinework: left } = lineworkDoorFolding(doorPanelLine, panelCount, panelAngle, -offset);
    doorBodyLinework.push(...left);
  }

  //folding doors from right jamb
  if (!side || door.descriptor.operationType === DoorOperationType.DOUBLE_BI_FOLD) {
    const rightPanelsStart = lineEndPt(baseline);
    const doorPanelLine = doorPanelBaseLine(baseline, -(3 * PI) / 4, rightPanelsStart, panelLength);
    const { doorBodyLinework: right } = lineworkDoorFolding(doorPanelLine, panelCount, -panelAngle, offset);
    doorBodyLinework.push(...right);
  }

  return doorOperationInfoNew({
    frontLeftIsLatchSide: !side,
    frontRightIsLatchSide: side,
    doorBodyLinework,
    planOutlineExteriorLinework: getDoorPlanOutlineExteriorLinework(door),
  });
}

function makeDoubleFixedPanelDoorOperationInfo(door: GeneratedDoorPlacement, swingAngle: number) {
  const baseline = getDoorOperationBaseline(door);
  const side = getDoorOperationSide(door);
  const [midX, midY] = line2Eval(baseline[0], baseline[1], baseline[2], baseline[3], 0.5, true);
  const firstLine = polyLine2New([baseline[0], baseline[1], midX, midY]);
  const secondLine = polyLine2New([midX, midY, baseline[2], baseline[3]]);

  const swingLine = side ? secondLine : firstLine;
  const panelLine = side ? firstLine : secondLine;

  const { doorBodyLinework, doorSwingLinework, doorBodyOBB, doorSwingLineworkOBB } = lineworkDoorSingle(
    swingLine,
    side,
    swingAngle,
  );
  const { doorBodyLinework: doorBodyFixedLinework } = lineworkDoorClosedPanel(panelLine, side);

  return doorOperationInfoNew({
    frontLeftIsLatchSide: !side,
    frontRightIsLatchSide: side,
    doorBodyLinework,
    doorSwingLinework,
    doorBodyFixedLinework,
    planOutlineExteriorLinework: getDoorPlanOutlineExteriorLinework(door),
    doorBodyOBBs: [doorBodyOBB],
    doorSwingLineworkOBBs: [doorSwingLineworkOBB],
  });
}

function makeWallMountedDoorOperationInfo(door: GeneratedDoorPlacement) {
  const baseline = getDoorOperationBaseline(door);
  const side = getDoorOperationSide(door);
  const [midX, midY] = line2Eval(baseline[0], baseline[1], baseline[2], baseline[3], 0.5, true);
  const [baselineNormX, baselineNormY] = vec2Normalize(baseline[2] - baseline[0], baseline[3] - baseline[1]);

  let panelLength = polyLine2Length(baseline);
  let spacing = 1;
  if (door.descriptor.doorLeafCount > 1) {
    panelLength = panelLength / 2;
    spacing = spacing + panelLength / 2;
  }

  const firstLineStartX = midX + spacing * baselineNormX;
  const firstLineStartY = midY + spacing * baselineNormY;
  const secondLineStartX = midX - spacing * baselineNormX;
  const secondLineStartY = midY - spacing * baselineNormY;

  const firstLine = polyLine2New([
    firstLineStartX + panelLength * baselineNormX,
    firstLineStartY + panelLength * baselineNormY,
    firstLineStartX,
    firstLineStartY,
  ]);
  const secondLine = polyLine2New([
    secondLineStartX,
    secondLineStartY,
    secondLineStartX + panelLength * -baselineNormX,
    secondLineStartY + panelLength * -baselineNormY,
  ]);

  const firstPanel = side ? secondLine : firstLine;
  const secondPanel = side ? firstLine : secondLine;

  const doorBodyLinework: PolyLine2[] = [];

  const { doorBodyLinework: firstDoorBodyLinework } = lineworkDoorClosedPanel(firstPanel, side);
  doorBodyLinework.push(...firstDoorBodyLinework);

  if (door.descriptor.doorLeafCount > 1) {
    const { doorBodyLinework: secondDoorBodyLinework } = lineworkDoorClosedPanel(secondPanel, side);
    doorBodyLinework.push(...secondDoorBodyLinework);
  }

  const doorHalfWidth = door.descriptor.width / 2;

  return doorOperationInfoNew({
    doorBodyLinework,
    planOutlineExteriorLinework: getDoorPlanOutlineExteriorLinework(door),
    frontLeftIsLatchSide: !side,
    frontRightIsLatchSide: side,
    frontRightSwing: door.reverse ? undefined : doorHalfWidth,
    frontLeftSwing: door.reverse ? undefined : doorHalfWidth,
    backRightSwing: door.reverse ? doorHalfWidth : undefined,
    backLeftSwing: door.reverse ? doorHalfWidth : undefined,
  });
}

function makeBypassDoorOperationInfo(door: GeneratedDoorPlacement, arrowOffset: number = 3) {
  const baseline = getDoorOperationBaseline(door);
  const side = getDoorOperationSide(door);

  let leafCount = door.descriptor.doorLeafCount ?? 2;
  if (leafCount < 2) {
    leafCount = 2;
  }
  const { doorBodyLinework } = lineworkDoorBypass(baseline, leafCount, side);

  const arrowRef = polyLine2Copy(baseline);
  const [nx, ny] = polyLine2EdgeNormal(arrowRef, 0);
  let [tx, ty] = polyLine2EdgeTangent(arrowRef, 0, true);
  let arrowRotation = vec2AngleBetween(1, 0, tx, ty);

  if (side) {
    polyLine2Reverse(arrowRef);
    arrowRotation = -(Math.PI - arrowRotation);
    tx = -tx;
    ty = -ty;
  }

  const leafLength = polyLine2Length(baseline) / leafCount;
  const tanOffset = (leafLength / 2) * (leafCount * 2 - 1);
  const moveX = arrowRef[0] - tx * tanOffset + nx * arrowOffset;
  const moveY = arrowRef[1] - ty * tanOffset + ny * arrowOffset;

  let mat = mat3Rotate(arrowRotation);
  mat = mat3Mult(mat3Translate(moveX, moveY), mat);
  const arrow = polyLine2Transform(polyLine2Copy(SliderOperationArrowDefault), mat);

  return doorOperationInfoNew({
    doorBodyLinework,
    sliderOperationArrowLinework: [arrow],
    planOutlineExteriorLinework: getDoorPlanOutlineExteriorLinework(door),
  });
}

function lineworkDoorFolding(panelBaseline: PolyLine2, panelCount: number, panelAngle: number, panelThickness: number) {
  const doorBodyLinework: PolyLine2[] = [];

  let evenPanel = true;
  for (let i = 0; i < panelCount; i++) {
    const extRect = polyLine2ThickenedEdge(panelBaseline, 0, panelThickness);
    doorBodyLinework.push(extRect);

    const endX = evenPanel ? extRect[6] : extRect[4];
    const endY = evenPanel ? extRect[7] : extRect[5];

    const nextPanelLine = polyLine2Rotate(
      polyLine2New([extRect[4], extRect[5], extRect[6], extRect[7]]),
      panelAngle,
      endX,
      endY,
    );

    panelBaseline = nextPanelLine;
    panelAngle = -panelAngle;
    evenPanel = !evenPanel;
  }
  return { doorBodyLinework };
}

function doorPanelBaseLine(baseline: PolyLine2, swingAngle: number, startPt: any, panelLength: number) {
  const ang = line2VecPerp(baseline, true);
  const [rotX, rotY] = vec2Rotate(ang[0], ang[1], swingAngle);
  const panelVec = polyLine2New(vec2Normalize(rotX, rotY));

  const doorPanelLine = line2SVL(startPt, panelVec, panelLength);
  return doorPanelLine;
}

const DOOR_BYPASS_DOOR_DEPTH = 1.5;
const DOOR_BYPASS_OVERLAP = 2;

function lineworkDoorBypass(line: PolyLine2, leafCount: number = 2, side: boolean = false) {
  const doorBodyLinework: PolyLine2[] = [];
  const baseline = polyLine2Copy(line);

  let [moveX, moveY] = vec2Perp(baseline[0] - baseline[2], baseline[1] - baseline[3]);
  [moveX, moveY] = vec2Normalize(moveX, moveY);
  let [tanX, tanY] = vec2Normalize(baseline[2] - baseline[0], baseline[3] - baseline[1]);

  const panelLength = (polyLine2Length(baseline) + (leafCount - 1) * DOOR_BYPASS_OVERLAP) / leafCount;
  const halfThickWall = WALL_THICKNESS_INTERIOR / 2;
  const halfThickDoor = (DOOR_BYPASS_DOOR_DEPTH * leafCount) / 2;

  let offset = halfThickWall - halfThickDoor + DOOR_BYPASS_DOOR_DEPTH;

  if (side === true) {
    polyLine2Reverse(baseline);
    tanX = -tanX;
    tanY = -tanY;
    offset -= DOOR_BYPASS_DOOR_DEPTH;
  }

  const startX = baseline[2] + moveX * offset;
  const startY = baseline[3] + moveY * offset;

  const panelLine = polyLine2New([startX, startY, startX - tanX * panelLength, startY - tanY * panelLength]);

  for (let i = 0; i < leafCount; i++) {
    const { doorBodyLinework: leafBodyLinework } = lineworkDoorClosedPanel(panelLine, false);
    doorBodyLinework.push(...leafBodyLinework);

    polyLine2Move(panelLine, moveX * DOOR_BYPASS_DOOR_DEPTH, moveY * DOOR_BYPASS_DOOR_DEPTH);
    polyLine2Extend(panelLine, -panelLength + DOOR_BYPASS_OVERLAP, panelLength - DOOR_BYPASS_OVERLAP);
  }

  return { doorBodyLinework };
}

function makeGarageDoorOperationInfo(door: GeneratedDoorPlacement) {
  const baseline = getDoorOperationBaseline(door);

  const doorBodyLinework: PolyLine2[] = [];
  const overheadOperationLinework: PolyLine2[] = [];

  const outline = polyLine2ThickenedEdge(baseline, 0, 80);
  overheadOperationLinework.push(outline.slice(2, 12));

  const doorOutline = polyLine2ThickenedEdge(baseline, 0, DEFAULT_DOOR_PANEL_THICKNESS);

  doorBodyLinework.push(doorOutline);

  return doorOperationInfoNew({
    doorBodyLinework,
    planOutlineExteriorLinework: getDoorPlanOutlineExteriorLinework(door),
    overheadOperationLinework,
  });
}

function makePocketDoorOperationInfo(door: GeneratedDoorPlacement) {
  const baseline = getPocketDoorOperationBaseline(door);

  const baselineLength = polyLine2Length(baseline);
  const doorLeafCountEffectivelyOne = door.descriptor.doorLeafCount < 2;
  const doorLeafCountEffectivelyTwo = door.descriptor.doorLeafCount > 1;

  let panelLength: number | undefined;
  let spacing: number | undefined;
  if (doorLeafCountEffectivelyOne) {
    panelLength = baselineLength;
    spacing = -baselineLength / 4;
  } else if (doorLeafCountEffectivelyTwo) {
    panelLength = baselineLength / 2;
    spacing = panelLength / 4;
  } else {
    return null;
  }

  const [midX, midY] = line2Eval(baseline[0], baseline[1], baseline[2], baseline[3], 0.5, true);
  const [baselineNormX, baselineNormY] = vec2Normalize(baseline[2] - baseline[0], baseline[3] - baseline[1]);
  const firstLineStartX = midX + spacing * baselineNormX;
  const firstLineStartY = midY + spacing * baselineNormY;
  const secondLineStartX = midX - spacing * baselineNormX;
  const secondLineStartY = midY - spacing * baselineNormY;

  const firstLine = polyLine2New([
    firstLineStartX + panelLength * baselineNormX,
    firstLineStartY + panelLength * baselineNormY,
    firstLineStartX,
    firstLineStartY,
  ]);
  const secondLine = polyLine2New([
    secondLineStartX,
    secondLineStartY,
    secondLineStartX + panelLength * -baselineNormX,
    secondLineStartY + panelLength * -baselineNormY,
  ]);

  const side = getDoorOperationSide(door);
  const firstPanel = side ? secondLine : firstLine;
  const secondPanel = side ? firstLine : secondLine;

  const doorBodyLinework: PolyLine2[] = [];
  const operationLinework: PolyLine2[] = [];

  const { doorBodyLinework: firstDoorBodyLinework } = lineworkDoorClosedPanel(firstPanel, side);
  doorBodyLinework.push(...firstDoorBodyLinework);

  const operationDist = (panelLength * 3) / 4;

  const normFlip = side ? -1 : 1;

  const firstOperationPanel = polyLine2MoveCopy(
    firstPanel,
    operationDist * baselineNormX * normFlip,
    operationDist * baselineNormY * normFlip,
  );

  const { doorBodyLinework: firstOperationLinework } = lineworkDoorClosedPanel(firstOperationPanel, side);
  operationLinework.push(...firstOperationLinework);

  if (door.descriptor.doorLeafCount > 1) {
    const { doorBodyLinework: secondDoorBodyLinework } = lineworkDoorClosedPanel(secondPanel, side);
    doorBodyLinework.push(...secondDoorBodyLinework);
    const secondOperationPanel = polyLine2MoveCopy(
      secondPanel,
      operationDist * baselineNormX * -normFlip,
      operationDist * baselineNormY * -normFlip,
    );

    const { doorBodyLinework: secondOperationLinework } = lineworkDoorClosedPanel(secondOperationPanel, side);
    operationLinework.push(...secondOperationLinework);
  }

  const resolvedSide = door.reverse ? !side : side;
  const isNotSideOfSingleOrIsDouble = (!resolvedSide && doorLeafCountEffectivelyOne) || doorLeafCountEffectivelyTwo;
  const isSideOfSingleOrIsDouble = (resolvedSide && doorLeafCountEffectivelyOne) || doorLeafCountEffectivelyTwo;
  const swingLength = door.descriptor.doorLeafCount > 1 ? baselineLength / 2 : baselineLength;

  return doorOperationInfoNew({
    doorBodyLinework,
    operationLinework,
    frontLeftIsLatchSide: isNotSideOfSingleOrIsDouble,
    frontRightIsLatchSide: isSideOfSingleOrIsDouble,
    frontRightSwing: isNotSideOfSingleOrIsDouble ? swingLength : undefined,
    frontLeftSwing: isSideOfSingleOrIsDouble ? swingLength : undefined,
    backRightSwing: isSideOfSingleOrIsDouble ? swingLength : undefined,
    backLeftSwing: isNotSideOfSingleOrIsDouble ? swingLength : undefined,
    doorSwingStopsAtCorners: true,
  });
}

export function lineworkDoorSingle(baseline: PolyLine2, side: boolean, swingAngle: number = 45) {
  const doorBodyLinework: PolyLine2[] = [];
  const doorSwingLinework: PolyLine2[] = [];
  let startBasePt;
  let endBasePt;

  if (side) {
    startBasePt = lineStartPt(baseline);
    endBasePt = lineEndPt(baseline);
  } else {
    startBasePt = lineEndPt(baseline);
    endBasePt = lineStartPt(baseline);
    swingAngle = swingAngle * -1;
  }
  const doorExtendLine = line2SVL(startBasePt, line2VecPerp(baseline, true), lineLength(baseline));

  polyLine2Rotate(doorExtendLine, degreeToRadian(swingAngle), startBasePt[0], startBasePt[1]);

  const arcEndPt = lineEndPt(doorExtendLine);
  const doorArc = arc2ByCenterStartAngle(
    startBasePt[0],
    startBasePt[1],
    arcEndPt[0],
    arcEndPt[1],
    degreeToRadian(swingAngle),
  );
  doorSwingLinework.push(doorArc);

  doorBodyLinework.push(doorExtendLine);
  const extRect = polyLine2ThickenedEdge(
    doorExtendLine,
    0,
    side ? -DEFAULT_DOOR_PANEL_THICKNESS : DEFAULT_DOOR_PANEL_THICKNESS,
  );
  doorBodyLinework.push(extRect);

  const doorBodyOBB = obb2ByThickenedEdge(
    doorExtendLine,
    0,
    side ? -DEFAULT_DOOR_PANEL_THICKNESS : DEFAULT_DOOR_PANEL_THICKNESS,
  );

  const arcRadius = vec2Dist(startBasePt[0], startBasePt[1], arcEndPt[0], arcEndPt[1]);
  const arcHeight =
    arcRadius - line2PerpDistance(endBasePt[0], endBasePt[1], arcEndPt[0], arcEndPt[1], startBasePt[0], startBasePt[1]);
  const doorSwingLineworkOBB = obb2ByThickenedEdge(
    polyLine2New([endBasePt[0], endBasePt[1], arcEndPt[0], arcEndPt[1]]),
    0,
    side ? -arcHeight : arcHeight,
  );

  return { doorBodyLinework, doorSwingLinework, doorBodyOBB, doorSwingLineworkOBB };
}

function lineworkDoorClosedPanel(line: PolyLine2, side: boolean = false) {
  const doorBodyLinework: PolyLine2[] = [];

  let startBasePt;
  let offsetSide;
  if (side) {
    startBasePt = lineStartPt(line);
    offsetSide = 1;
  } else {
    startBasePt = lineEndPt(line);
    offsetSide = -1;
  }
  const doorExtendLine = line2SVL(startBasePt, line2VecPerp(line, true), -DEFAULT_DOOR_PANEL_THICKNESS);
  doorBodyLinework.push(doorExtendLine);

  const extRect = polyLine2ThickenedEdge(doorExtendLine, 0, lineLength(line) * offsetSide);
  doorBodyLinework.push(extRect);

  return { doorBodyLinework };
}

function sliderOperationArrowNew(): PolyLine2 {
  const arrowLength = 10;
  const arrowHeadLength = 3;
  const arrowHeadWidth = DEFAULT_DOOR_PANEL_THICKNESS;
  const arrowTailWidth = 0.75;

  return polyLine2New([
    0,
    arrowTailWidth / 2,
    arrowLength - arrowHeadLength,
    arrowTailWidth / 2,
    arrowLength - arrowHeadLength,
    arrowHeadWidth / 2,
    arrowLength,
    0,
    arrowLength - arrowHeadLength,
    -arrowHeadWidth / 2,
    arrowLength - arrowHeadLength,
    -arrowTailWidth / 2,
    0,
    -arrowTailWidth / 2,
    0,
    arrowTailWidth / 2,
  ]);
}

const SliderOperationArrowDefault = sliderOperationArrowNew();
